import { isNumber, parseIntOrNull } from './numbers';

function toggleValueInArray<T>(list: T[], value: T, isEqual?: (value1: T, value2: T) => boolean) {
  const newList = [...list];
  const valueIndex = isEqual ? list.findIndex(v => isEqual(v, value)) : list.indexOf(value);
  if (valueIndex >= 0) {
    newList.splice(valueIndex, 1);
  } else {
    newList.push(value);
  }
  return newList;
}

function updateOrInsertInArray<T>(
  list: T[],
  value: T,
  isEqual: (value1: T, value2: T) => boolean = (value1, value2) => value1 === value2
) {
  let hasAValueBeenFoundAndModified = false;

  const newList = list.map(listValue => {
    if (isEqual(listValue, value)) {
      hasAValueBeenFoundAndModified = true;
      return value;
    }
    return listValue;
  });

  if (!hasAValueBeenFoundAndModified) {
    newList.push(value);
  }

  return newList;
}

function updateValueInArray<T>(
  list: T[],
  updateFunction: (originalValue: T) => T,
  isSearchedValue: (value1: T, index?: number) => boolean
) {
  return list.map((value, index) => {
    if (isSearchedValue(value, index)) {
      return updateFunction(value);
    }
    return value;
  });
}

function removeItem<T>(list: T[], value: T): T[] {
  const index = list.indexOf(value);
  if (index >= 0) {
    const newList = [...list];
    newList.splice(index, 1);
    return newList;
  }
  return list;
}

function removeItemWhen<T>(list: T[], predicate: (value: T) => boolean) {
  const index = list.findIndex(predicate);
  if (index >= 0) {
    const newList = [...list];
    newList.splice(index, 1);
    return newList;
  }
  return list;
}

function createArray(length: number): undefined[] {
  return new Array(length).fill(undefined);
}

function compareStrings(slugA: string, slugB: string): number {
  const parsedSlugA = parseIntOrNull(slugA);
  const parsedSlugB = parseIntOrNull(slugB);

  if (isNumber(parsedSlugA) && isNumber(parsedSlugB)) {
    return parsedSlugA - parsedSlugB;
  }

  if (slugA > slugB) return 1;
  if (slugA < slugB) return -1;
  return 0;
}

const compareValues = <T>(valueA: T, valueB: T, nullValueAtFirst = true, nullValue: any = null) => {
  if (!nullValueAtFirst) {
    if (valueA == nullValue && valueB == nullValue) {
      return 0;
    }
    if (valueA == nullValue) {
      return 1;
    }
    if (valueB == nullValue) {
      return -1;
    }
  }
  if (valueA == nullValue && valueB == nullValue) {
    return 0;
  }
  if (valueA == nullValue) {
    return -1;
  }
  if (valueB == nullValue) {
    return 1;
  }
  if (valueA > valueB) {
    return 1;
  }
  if (valueA < valueB) {
    return -1;
  }
  return 0;
};

function sortBy<T extends object>(
  list: T[],
  key: keyof T,
  nullValueAtFirst?: boolean,
  nullValue?: ''
): T[] {
  return [...list].sort((itemA, itemB) => {
    const valueA = itemA[key];
    const valueB = itemB[key];

    return compareValues(valueA, valueB, nullValueAtFirst, nullValue);
  });
}

function sortByGetProp<T extends object>(list: T[], getProp: (value: T) => any): T[] {
  return [...list].sort((itemA, itemB) => {
    const valueA = getProp(itemA);
    const valueB = getProp(itemB);

    return compareValues(valueA, valueB);
  });
}

function intersperse<T, U>(array: T[], sep: U): (T | U)[] {
  const newArray = array.reduce((acc, value) => {
    acc.push(sep);
    acc.push(value);
    return acc;
  }, [] as (T | U)[]);
  newArray.splice(0, 1);

  return newArray;
}

function listWithMinimumLength<T>(
  list: T[],
  params: { minLength: number; createItem: (index: number) => T }
): T[] {
  const nbItemsToCreate = Math.max(0, params.minLength - list.length);

  if (nbItemsToCreate > 0) {
    const emptyArray = createArray(nbItemsToCreate);
    const newItems: T[] = emptyArray.map((_, index) => params.createItem(index));

    return list.concat(newItems);
  }
  return list;
}

const updateListWithNewItems = <T>(
  list: T[],
  newItems: T[],
  predicate: (listItem: T, newItem: T) => boolean
) =>
  list.map(listItem => {
    const replacementItem = newItems.find(newItem => predicate(listItem, newItem));
    return replacementItem ?? listItem;
  });

const findLastInArray = <T>(array: T[], predicate: (value: T) => boolean): T | undefined => {
  let index = array.length;
  while (index) {
    index -= 1;
    const condition = predicate(array[index]);
    if (condition) {
      return array[index];
    }
  }
  return undefined;
};

const partitionArray = <T, K extends string>(
  array: T[],
  predicate: (element: T) => K
): Record<K, T[] | undefined> =>
  array.reduce((partition: Record<K, T[]>, element) => {
    const key = predicate(element);
    partition[key] = partition[key] ? [...partition[key], element] : [element];
    return partition;
  }, {} as Record<K, T[]>);

const splitArray = <T>(array: T[], predicate: (element: T) => 0 | 1 | null): [T[], T[]] =>
  array.reduce(
    (partition: [T[], T[]], element) => {
      const index = predicate(element);
      if (index === null) {
        return partition;
      }
      partition[index].push(element);
      return partition;
    },
    [[], []]
  );

const splitArrayAtIndex = <T>(array: T[], index: number): [T[], T[]] => {
  const firstPart = array.slice(0, index);
  const secondPart = array.slice(index);
  return [firstPart, secondPart];
};

const asModel =
  <T, M extends string | number | null>(model: M[], getProp: (item: T) => M) =>
  (a: T, b: T) => {
    const indexOfA = model.indexOf(getProp(a));
    const indexOfB = model.indexOf(getProp(b));
    if (indexOfA === -1) return 1;
    if (indexOfB === -1) return -1;
    return indexOfA - indexOfB;
  };

const sortAsModel = <T, M extends string | number>(
  array: T[],
  model: M[],
  getProp: (item: T) => M
) => [...array].sort(asModel(model, getProp));

const removeDuplicates = <T extends object>(array: T[], keyToCompare: keyof T): T[] =>
  array.filter(
    (item, index, array) =>
      array.findIndex(searchedItem => searchedItem[keyToCompare] === item[keyToCompare]) === index
  );

const uniques = <T extends string | number>(array: T[]): T[] => Array.from(new Set(array));

const containTheSame = <T>(a: T[], b: T[]) =>
  a.every(item => b.includes(item)) && b.every(item => a.includes(item));

const extractProps = <T extends object, K extends keyof T>(list: T[], prop: K[]): Pick<T, K>[] =>
  list.map(item => prop.reduce((acc, key) => ({ ...acc, [key]: item[key] }), {} as Pick<T, K>));

// take a list of items and a number and return an array of arrays of the given size
const chunkArray = <T>(array: T[], size: number): T[][] => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

export {
  toggleValueInArray,
  updateOrInsertInArray,
  removeItem,
  removeItemWhen,
  createArray,
  compareStrings,
  compareValues,
  sortBy,
  intersperse,
  listWithMinimumLength,
  updateListWithNewItems,
  findLastInArray,
  partitionArray,
  sortAsModel,
  asModel,
  removeDuplicates,
  splitArray,
  uniques,
  containTheSame,
  updateValueInArray,
  splitArrayAtIndex,
  sortByGetProp,
  extractProps,
  chunkArray
};
